import jsPDF from 'jspdf';
import i18n from '../../../i18n';
import { BreakdownInfo } from '../../../pages/Home/types';
import { SceneWithUserNames } from '../../../types/scene.types';
import { ElementInfo, ElementType } from '../../../types/element.types';
import { CalDateInfo, SlotInfo, SlotType, ScheduleInfo } from '../../../types/schedule.types';
import { ElementDataCache, ElementRelationDataCache } from '../../../types/cache.types';
import { buildDisplaySegments } from '../../../utils/calDateSlotsSortUtils';
import { sortCastItemsByNO, sortStringsByExportNameRule } from '../../../utils/elementSortUtils';
import { buildDayRoleSummary } from '../../../utils/scheduleDayRoleSummary';
import { registerFont, setFont, SupportedLanguage } from '../PDFExporter';
import { formatDayNightForExport, formatInteriorExteriorForExport } from '../exportSceneFieldFormat';

// @ts-ignore
const autoTable = require('jspdf-autotable').default;

const t = (key: string, opts?: { lng?: string; [k: string]: unknown }) => i18n.t(key, opts);

/** 与 PDF-BreakdownDetail-A4 页眉一致 */
const PAGE = {
  MARGIN: 10,
  HEADER_FONT_SIZE: 8,
  HEADER_Y: 10,
  START_Y: 14,
};

export const PDF_SCHEDULE_DETAIL_A4_MARGINS_MM = {
  left: PAGE.MARGIN,
  right: PAGE.MARGIN,
  top: PAGE.START_Y,
  bottom: PAGE.MARGIN
} as const;

const FONT_FILES: Record<SupportedLanguage, { name: string }> = {
  'zh-CN': { name: 'AlibabaPuHuiTi' },
  'zh-HK': { name: 'AlibabaPuHuiTi' },
  en: { name: 'AlibabaPuHuiTi' },
};

const SCENE_LIST_BANNER_FONT_PT = 10;
const SCHEDULE_DETAIL_BODY_TEXT_COLOR: [number, number, number] = [0, 0, 0];

type DayRow =
  | { kind: 'scene'; scene: SceneWithUserNames }
  | { kind: 'note'; note: string };

function getShootingDates(scheduleSolts: SlotInfo[]): string[] {
  const slotDates = scheduleSolts
    .filter((pt) => pt.slotType === SlotType.SHOOT || !pt.slotType)
    .map((pt) => pt.slotDate?.split('T')[0] || '')
    .filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s));
  return Array.from(new Set(slotDates)).sort();
}

/** 导出用 YYYY-MM-DD + 星期（与语言环境一致） */
function formatYmdWithWeekday(
  dateStr: string,
  lng: SupportedLanguage,
  secondaryLng?: SupportedLanguage
): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!m) return dateStr;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const date = new Date(y, mo - 1, d);
  if (Number.isNaN(date.getTime())) return dateStr;
  const iso = `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const locale = lng === 'en' ? 'en-US' : lng === 'zh-HK' ? 'zh-HK' : 'zh-CN';
  const wd = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date);
  if (!secondaryLng || secondaryLng === lng) return `${iso} ${wd}`;
  const localeSecondary = secondaryLng === 'en' ? 'en-US' : secondaryLng === 'zh-HK' ? 'zh-HK' : 'zh-CN';
  const wdSecondary = new Intl.DateTimeFormat(localeSecondary, { weekday: 'long' }).format(date);
  return `${iso} ${wd} ${wdSecondary}`;
}

/**
 * 文档页眉（与 BreakdownDetail 类似）：《片名》计划版本… 分场版本… ｜ 剧本版本…｜第 X 页
 */
function addScheduleDetailDocHeader(
  doc: jsPDF,
  language: SupportedLanguage,
  fontName: string,
  scheduleInfo: ScheduleInfo | null,
  breakdownInfo: BreakdownInfo,
  pdfPageNumber: number
) {
  const pageW = doc.internal.pageSize.getWidth();
  const startX = PAGE.MARGIN;
  const titleY = PAGE.HEADER_Y;
  const filmName = typeof window !== 'undefined' ? localStorage.getItem('selectedFilmName') || '' : '';
  const colon = t('common.labelSeparator', { lng: language }) as string;
  const scheduleVersionText = (scheduleInfo?.scheduleName || '').trim();
  const breakdownName = (breakdownInfo.breakdownName || '').trim();
  const scriptOverview = (breakdownInfo.breakdownScriptOverview || '').trim();
  const scheduleVersionLabel = `${t('export.castPdfScheduleVersionLabel', { lng: language })}${colon}`;
  const breakdownLabel = `${t('export.castPdfBreakdownVersionLabel', { lng: language })}${colon}`;
  const leftTitleText = `《${filmName}》${scheduleVersionLabel}${scheduleVersionText}         ${breakdownLabel}${breakdownName}`;

  doc.setFontSize(PAGE.HEADER_FONT_SIZE);
  setFont(doc, language, 'normal');
  doc.setFont(fontName, 'normal');
  doc.setTextColor(...SCHEDULE_DETAIL_BODY_TEXT_COLOR);
  doc.text(leftTitleText, startX, titleY);

  const scriptLabel = t('export.scriptVersion', { lng: language });
  const pagePrefix = t('export.pagePrefix', { lng: language });
  const pageSuffix = t('export.pageSuffix', { lng: language });
  const scriptSep = t('common.labelSeparator', { lng: language }) as string;
  let rightTitleText = `${scriptLabel}${scriptSep}${scriptOverview}`;
  rightTitleText += `${t('common.pipeSeparator', { lng: language })}${pagePrefix} ${pdfPageNumber}${pageSuffix ? ` ${pageSuffix}` : ''}`;

  doc.setFontSize(PAGE.HEADER_FONT_SIZE);
  setFont(doc, language, 'normal');
  doc.setFont(fontName, 'normal');
  doc.setTextColor(...SCHEDULE_DETAIL_BODY_TEXT_COLOR);
  const rightTextWidth = doc.getTextWidth(rightTitleText);
  const rightX = pageW - rightTextWidth - startX;
  doc.text(rightTitleText, rightX, titleY);
}

function getCurrentDocPageNumber(doc: jsPDF): number {
  const internal = (doc as jsPDF & {
    internal?: { getCurrentPageInfo?: () => { pageNumber?: number }; getNumberOfPages?: () => number };
  }).internal;
  const pageInfo = internal?.getCurrentPageInfo?.();
  return Number(pageInfo?.pageNumber) || Number(internal?.getNumberOfPages?.()) || 1;
}

/** 场次列表首行：左 第X/Y日｜中 日期+星期(粗)｜右 共X页 */
function drawScheduleDetailSceneListBannerRow(
  doc: jsPDF,
  language: SupportedLanguage,
  fontName: string,
  cell: { x: number; y: number; width: number; height: number },
  dateStr: string,
  secondaryLanguage: SupportedLanguage | undefined,
  dayIndex: number,
  dayTotal: number,
  pageCountLabel: string
) {
  const padL = 1;
  const padR = 1;
  const xL = cell.x + padL;
  const xR = cell.x + cell.width - padR;
  const midX = cell.x + cell.width / 2;
  const k = doc.internal.scaleFactor;
  const yMid = cell.y + cell.height / 2;
  const yBaseline = yMid + (SCENE_LIST_BANNER_FONT_PT / k) * 0.35;

  const leftText = t('export.shootScheduleDetailDayProgress', {
    lng: language,
    current: dayIndex,
    total: dayTotal
  });
  const centerText = formatYmdWithWeekday(dateStr, language, secondaryLanguage);

  doc.setFontSize(SCENE_LIST_BANNER_FONT_PT);
  doc.setTextColor(...SCHEDULE_DETAIL_BODY_TEXT_COLOR);

  setFont(doc, language, 'normal');
  doc.setFont(fontName, 'normal');
  doc.text(leftText, xL, yBaseline);

  setFont(doc, language, 'bold');
  doc.setFont(fontName, 'bold');
  doc.text(centerText, midX, yBaseline, { align: 'center' });

  setFont(doc, language, 'normal');
  doc.setFont(fontName, 'normal');
  doc.text(pageCountLabel, xR, yBaseline, { align: 'right' });
}

function mapSceneById(scenes: SceneWithUserNames[]): Map<string, SceneWithUserNames> {
  const m = new Map<string, SceneWithUserNames>();
  scenes.forEach((s) => m.set(s.sceneID, s));
  return m;
}

function buildDayRows(
  dateStr: string,
  scheduleSolts: SlotInfo[],
  dayCalendarDay: CalDateInfo | undefined,
  scenes: SceneWithUserNames[]
): DayRow[] {
  const daySolts = scheduleSolts.filter((t) => t.slotDate?.split('T')[0] === dateStr);
  const slotMap = new Map(daySolts.map((t) => [t.slotID, t]));
  const sceneMap = mapSceneById(scenes);
  const segments = buildDisplaySegments(daySolts, dayCalendarDay?.calDateSlotsSort, dateStr, (sceneID) => {
    const s = sceneID ? sceneMap.get(sceneID) : undefined;
    return s ? { sceneLocation: s.sceneLocation } : undefined;
  });
  const rows: DayRow[] = [];
  const usedBreakdown = new Set<string>();
  const usedNotes = new Set<string>();
  for (const seg of segments) {
    if (seg.kind === 'note') {
      for (const id of seg.items) {
        const slot = slotMap.get(id);
        const text = (slot?.slotName || '').trim();
        if (text && !usedNotes.has(id)) {
          rows.push({ kind: 'note', note: text });
          usedNotes.add(id);
        }
      }
    } else {
      for (const id of seg.items) {
        const slot = slotMap.get(id);
        if (!slot?.slotBelongSceneID) continue;
        const sid = slot.slotBelongSceneID;
        const scene = sceneMap.get(sid);
        if (scene && !usedBreakdown.has(sid)) {
          rows.push({ kind: 'scene', scene });
          usedBreakdown.add(sid);
        }
      }
    }
  }
  return rows;
}

function lineHeightMm(doc: jsPDF, fontSizePt: number): number {
  const k = doc.internal.scaleFactor;
  const lhf =
    typeof (doc as jsPDF & { getLineHeightFactor?: () => number }).getLineHeightFactor === 'function'
      ? (doc as jsPDF & { getLineHeightFactor: () => number }).getLineHeightFactor()
      : 1.15;
  return (fontSizePt / k) * lhf;
}

function buildNameQtyRows(items: Array<{ name: string; qty: number }>): string {
  return items
    .map((it) => {
      const name = (it.name || '').trim();
      return name ? `${name} *${it.qty}` : `*${it.qty}`;
    })
    .join('\n');
}

/** 与 jspdf-autotable 本页 role / dept 表一致：cellPadding 1.2 */
const SCHEDULE_DETAIL_TABLE_CELL_PAD_MM = 1.2;

function estimateAutoTableHeadRowHeightMm(doc: jsPDF, fontSizePt: number): number {
  return SCHEDULE_DETAIL_TABLE_CELL_PAD_MM * 2 + lineHeightMm(doc, fontSizePt);
}

/** 正文区至少一行（含上下 padding），用于判断是否只剩表头位 */
function estimateAutoTableMinBodyFloorMm(doc: jsPDF, fontSizePt: number): number {
  return SCHEDULE_DETAIL_TABLE_CELL_PAD_MM * 2 + lineHeightMm(doc, fontSizePt);
}

/**
 * 若当前页剩余高度不足以放置「表头 + 至少一行正文」，则整表（含表头）换到下一页，
 * 避免出现上一页仅有表头、正文从下一页才开始。
 */
function adjustStartYIfTableHeaderWouldOrphan(
  doc: jsPDF,
  proposedStartY: number,
  bottomMarginMm: number,
  fontSizePt: number
): number {
  const pageH = doc.internal.pageSize.getHeight();
  const remaining = pageH - proposedStartY - bottomMarginMm;
  const headH = estimateAutoTableHeadRowHeightMm(doc, fontSizePt);
  const minBelow = estimateAutoTableMinBodyFloorMm(doc, fontSizePt);
  const gridLineFudgeMm = 0.8;
  if (remaining < headH + minBelow + gridLineFudgeMm) {
    doc.addPage();
    return PAGE.START_Y;
  }
  return proposedStartY;
}

/** 与单格内绘制顺序一致的一行（用于与 jspdf-autotable 按行分页对齐） */
/** boldLead：类型名 + i18n 冒号（仅首段粗体）；lw：粗体前缀宽度，续行正文左缩进与之对齐 */
type ScheduleDetailDeptVisualLine = { boldLead: string; lw: number; text: string };

function buildScheduleDetailDeptVisualLines(
  doc: jsPDF,
  fontName: string,
  fontSizePt: number,
  innerWidthMm: number,
  typeColon: string,
  rows: OtherSceneDeptRow[]
): ScheduleDetailDeptVisualLine[] {
  const pad = 1.2;
  const x0 = pad;
  const innerR = innerWidthMm - pad;
  doc.setFontSize(fontSizePt);
  const out: ScheduleDetailDeptVisualLine[] = [];
  rows.forEach((r) => {
    const boldLeadFull = `${r.typeLabel}${typeColon}`;
    doc.setFont(fontName, 'bold');
    const lw = doc.getTextWidth(boldLeadFull);
    doc.setFont(fontName, 'normal');
    const namesOnly = r.namesText;
    const wFirst = Math.max(8, innerR - x0 - lw - 0.5);
    const wrapped = (doc.splitTextToSize(namesOnly, wFirst) as string[]) || [];
    const parts = wrapped.length > 0 ? wrapped : [namesOnly];
    parts.forEach((part, li) => {
      out.push({
        boldLead: li === 0 ? boldLeadFull : '',
        lw,
        text: part,
      });
    });
  });
  doc.setFont(fontName, 'normal');
  return out;
}

/** 绘制当前单元格片段对应的视觉行（与原先 drawScheduleDetailDeptSingleCell 版面一致） */
function drawScheduleDetailDeptVisualLineSlice(
  doc: jsPDF,
  fontName: string,
  fontSizePt: number,
  cell: { x: number; y: number; width: number; height: number },
  lines: ScheduleDetailDeptVisualLine[]
): void {
  const pad = 1.2;
  const lh = lineHeightMm(doc, fontSizePt);
  const k = doc.internal.scaleFactor;
  const x0 = cell.x + pad;
  let y = cell.y + pad + fontSizePt / k;
  doc.setFontSize(fontSizePt);
  // 与场次表、演员表正文文字颜色保持一致
  doc.setTextColor(...SCHEDULE_DETAIL_BODY_TEXT_COLOR);
  for (const line of lines) {
    if (line.boldLead) {
      doc.setFont(fontName, 'bold');
      doc.text(line.boldLead, x0, y);
      doc.setFont(fontName, 'normal');
      doc.text(line.text, x0 + line.lw + 0.5, y);
    } else if (line.text) {
      doc.setFont(fontName, 'normal');
      // 续行不再按类型名前缀缩进，直接顶格拼接
      doc.text(line.text, x0, y);
    }
    y += lh;
  }
}

/** 与 PDF-BreakdownDetail-A4 分组顺序一致（不含主要/特约/群演/动作） */
const SCHEDULE_DETAIL_OTHER_ITEM_TYPE_ORDER: ElementType[] = [
  ElementType.ANIMALS,
  ElementType.VEHICLES,
  ElementType.ART_PROPS,
  ElementType.WARDROBE,
  ElementType.HAIR_MAKEUP,
  ElementType.SFX_MU,
  ElementType.SFX,
  ElementType.VFX,
  ElementType.CAMERA,
  ElementType.LIGHTING,
  ElementType.SOUND,
  ElementType.ADDL_LABOR,
  ElementType.SPECIAL_EQUIPMENT,
  ElementType.MISC,
  ElementType.NOTE
];

type OtherSceneDeptRow = { typeLabel: string; namesText: string };

function buildOtherElementsDeptRows(
  daySceneIds: Set<string>,
  elements: ElementInfo[],
  elementRelations: ElementRelationDataCache['elementRelations'],
  language: SupportedLanguage
): OtherSceneDeptRow[] {
  const itemById = new Map<string, ElementInfo>();
  elements.forEach((it) => itemById.set(it.elementID, it));
  const groupsByType = new Map<ElementType, Map<string, number>>();

  elementRelations.forEach((rel) => {
    if (!daySceneIds.has(rel.sceneID) || !rel.elementID) return;
    const item = itemById.get(rel.elementID);
    if (!item?.elementType) return;
    if (
      item.elementType === ElementType.CAST ||
      item.elementType === ElementType.FEATURED ||
      item.elementType === ElementType.BACKGROUND ||
      item.elementType === ElementType.STUNT_PLAYERS
    ) {
      return;
    }
    const name = (item.elementName || item.elementAbbr || '').trim();
    if (!name) return;
    let qtyByName = groupsByType.get(item.elementType);
    if (!qtyByName) {
      qtyByName = new Map<string, number>();
      groupsByType.set(item.elementType, qtyByName);
    }
    const prevQty = qtyByName.get(name) || 0;
    const currentQty = Math.max(1, rel.elementQtyInScene || 1);
    qtyByName.set(name, Math.max(prevQty, currentQty));
  });

  const rows: OtherSceneDeptRow[] = [];
  for (const typ of SCHEDULE_DETAIL_OTHER_ITEM_TYPE_ORDER) {
    const qtyByName = groupsByType.get(typ);
    if (!qtyByName || qtyByName.size === 0) continue;
    const typeLabel = t(`elementType.${typ}`, { lng: language });
    const names = Array.from(qtyByName.keys());
    const namesSorted = sortStringsByExportNameRule(names);
    const namesText = namesSorted
      .map((name) => {
        if (typ !== ElementType.ANIMALS && typ !== ElementType.VEHICLES) return name;
        const qty = Math.max(1, qtyByName.get(name) || 1);
        return `${name} *${qty}`;
      })
      .join(' / ');
    rows.push({ typeLabel, namesText });
  }
  return rows;
}

export interface ExportScheduleDetailParams {
  language: SupportedLanguage;
  secondaryLanguage?: SupportedLanguage;
  scenes: SceneWithUserNames[];
  breakdownInfo: BreakdownInfo;
  scheduleInfo: ScheduleInfo | null;
  scheduleSolts: SlotInfo[];
  calendarDays: CalDateInfo[];
  exportRange?: { start: string; end: string };
  optionName?: string;
  breakdownId?: string;
  getElementCache?: (breakdownId: string) => ElementDataCache | null;
  getElementRelationCache?: (breakdownId: string) => ElementRelationDataCache | null;
  onProgress?: (n: number) => void;
}

export async function exportScheduleDetailA4Document(
  initialDoc: jsPDF,
  params: ExportScheduleDetailParams
): Promise<jsPDF> {
  const {
    language,
    secondaryLanguage,
    scenes,
    breakdownInfo,
    scheduleInfo,
    scheduleSolts,
    calendarDays,
    exportRange,
    breakdownId,
    getElementCache,
    getElementRelationCache,
    onProgress,
  } = params;

  let doc = new jsPDF('p', 'mm', 'a4');
  const fontRegistered = await registerFont(doc, language);
  setFont(doc, language);
  const fontName = fontRegistered ? FONT_FILES[language].name : 'helvetica';
  void initialDoc;
  onProgress?.(15);

  const allShootingDates = getShootingDates(scheduleSolts);
  const shootingDates = exportRange
    ? allShootingDates.filter((d) => d >= exportRange.start && d <= exportRange.end)
    : allShootingDates;
  const elements = breakdownId && getElementCache ? getElementCache(breakdownId)?.elements || [] : [];
  const elementRelations =
    breakdownId && getElementRelationCache ? getElementRelationCache(breakdownId)?.elementRelations || [] : [];
  const elementById = new Map<string, ElementInfo>();
  elements.forEach((si) => elementById.set(si.elementID, si));
  shootingDates.forEach((dateStr, idx) => {
    if (idx > 0) doc.addPage();
    const dayIndex = idx + 1;

    const dayCalendarDay = calendarDays.find((pd) => pd.calDateDt?.split('T')[0] === dateStr);
    const dayRows = buildDayRows(dateStr, scheduleSolts, dayCalendarDay, scenes);
    const daySceneIds = new Set<string>(dayRows.filter((r): r is { kind: 'scene'; scene: SceneWithUserNames } => r.kind === 'scene').map((r) => r.scene.sceneID));
    const roleSummary = buildDayRoleSummary(daySceneIds, elements, elementRelations);
    const otherDeptRows = buildOtherElementsDeptRows(daySceneIds, elements, elementRelations, language);

    // 前四列与 PDF-BreakdownDetail-A4（Detailed Breakdown）主表同宽：10% / 7% / 7% / 38%（tableWidth = 页宽 − 左右各 10mm）。
    // 「排期」为右侧半块（19%）的一半；余宽给「拍摄地点」。页数列、排期列略增宽，宽度仅从拍摄地点列扣除。
    const tableWidthMm = doc.internal.pageSize.getWidth() - PAGE.MARGIN * 2;
    const wCode = tableWidthMm * 0.1;
    const wIoDay = tableWidthMm * 0.07;
    const wSetPlot = tableWidthMm * 0.38;
    const wRightBlock = tableWidthMm * 0.38;
    const wPeriodBase = wRightBlock / 2;
    const EXTRA_PAGES_AND_PERIOD_MM = 1.5;
    const wDnPages = tableWidthMm * 0.07 + EXTRA_PAGES_AND_PERIOD_MM / 2;
    const wPeriod = wPeriodBase * 0.5 + EXTRA_PAGES_AND_PERIOD_MM / 2;
    const wLocation =
      tableWidthMm - wCode - wIoDay - wDnPages - wSetPlot - wPeriod;
    const slotColumnWidths = {
      code: wCode,
      col2: wIoDay,
      col3: wDnPages,
      col4: wSetPlot,
      col5: wLocation,
      col6: wPeriod
    };
    const listBody: any[] = [];
    let dayPagesTotal = 0;
    dayRows.forEach((row) => {
      if (row.kind === 'note') {
        const noteText = (row.note || '').trim();
        listBody.push([
          {
            content: noteText,
            colSpan: 6,
            styles: {
              textColor: 0,
              fontStyle: 'normal',
              halign: 'left',
              valign: 'top',
              overflow: 'linebreak'
            }
          },
          '',
          '',
          '',
          '',
          ''
        ]);
        return;
      }
      const scene = row.scene;
      const pagesNum = Number(scene.scenePagesNumber ?? 0);
      if (!Number.isNaN(pagesNum)) dayPagesTotal += pagesNum;
      const castAbbrLine = sortCastItemsByNO(
        elementRelations
          .filter((rel) => rel.sceneID === scene.sceneID)
          .map((rel) => {
            const item = elementById.get(rel.elementID);
            return {
              elementID: rel.elementID,
              sceneID: rel.sceneID,
              elementType: item?.elementType,
              elementName: item?.elementName,
              elementAbbr: item?.elementAbbr,
              elementNO: item?.elementNO,
            };
          })
          .filter((x) => x.elementType === ElementType.CAST)
      )
        .map((x) => x.elementAbbr || x.elementName || '')
        .filter(Boolean)
        .join('/');
      const sceneCode = (scene.sceneCode || '').trim();
      const ioText = formatInteriorExteriorForExport(
        language,
        scene.sceneInteriorExterior,
        secondaryLanguage
      );
      const dnText = formatDayNightForExport(language, scene.sceneDayNight, secondaryLanguage);
      const setText = (scene.sceneSet || '').trim();
      const addrText = (scene.sceneLocation || '').trim();
      const periodText = (scene.scenePeriod || '').trim();
      const dayText = (scene.scenePlotDay || '').trim();
      const pagesText = scene.scenePagesNumber?.toString() || '';
      const plotText = (scene.scenePlot || '').trim();
      const castMergedCellText = castAbbrLine;

      listBody.push([
        {
          content: sceneCode,
          rowSpan: 2,
          styles: {
            valign: 'top',
            halign: 'left',
            overflow: 'linebreak',
            lineWidth: { top: 0.2, right: 0, bottom: 0.2, left: 0.2 }
          }
        },
        {
          content: ioText,
          styles: {
            halign: 'left',
            valign: 'top',
            overflow: 'linebreak',
            lineWidth: { top: 0.2, right: 0, bottom: 0, left: 0 }
          }
        },
        {
          content: dnText,
          styles: {
            halign: 'left',
            valign: 'top',
            overflow: 'linebreak',
            lineWidth: { top: 0.2, right: 0, bottom: 0, left: 0 }
          }
        },
        {
          content: setText,
          styles: {
            halign: 'left',
            valign: 'top',
            overflow: 'linebreak',
            lineWidth: { top: 0.2, right: 0, bottom: 0, left: 0 }
          }
        },
        {
          content: addrText,
          styles: {
            halign: 'left',
            valign: 'top',
            overflow: 'linebreak',
            lineWidth: { top: 0.2, right: 0, bottom: 0, left: 0 }
          }
        },
        {
          content: periodText,
          styles: {
            halign: 'left',
            valign: 'top',
            overflow: 'linebreak',
            lineWidth: { top: 0.2, right: 0.2, bottom: 0, left: 0 }
          }
        }
      ]);
      listBody.push([
        {
          content: dayText,
          styles: {
            halign: 'left',
            valign: 'top',
            overflow: 'linebreak',
            lineWidth: { top: 0, right: 0, bottom: 0.2, left: 0 }
          }
        },
        {
          content: pagesText,
          styles: {
            halign: 'left',
            valign: 'top',
            overflow: 'linebreak',
            lineWidth: { top: 0, right: 0, bottom: 0.2, left: 0 }
          }
        },
        {
          content: plotText,
          styles: {
            halign: 'left',
            valign: 'top',
            overflow: 'linebreak',
            lineWidth: { top: 0, right: 0, bottom: 0.2, left: 0 }
          }
        },
        {
          content: castMergedCellText,
          colSpan: 2,
          styles: {
            halign: 'left',
            valign: 'top',
            overflow: 'linebreak',
            lineWidth: { top: 0, right: 0.2, bottom: 0.2, left: 0 }
          }
        },
        ''
      ]);
    });

    const dayPagesText = dayPagesTotal.toFixed(1).replace(/\.0$/, '');
    const pageCountHeader = t('export.shootingSoltListPageCount', {
      lng: language,
      count: dayPagesText,
      unit: t('schedule.pages', { lng: language })
    });
    const sceneListBannerHeadCell = {
      content: '\u00a0',
      colSpan: 6,
      _scheduleDetailSceneBanner: true,
      styles: { minCellHeight: 7, valign: 'middle' as const, halign: 'left' as const }
    };

    autoTable(doc, {
      startY: PAGE.START_Y,
      theme: 'grid',
      margin: { left: PAGE.MARGIN, right: PAGE.MARGIN, top: PAGE.START_Y, bottom: PAGE.MARGIN },
      showHead: 'firstPage',
      rowPageBreak: 'avoid',
      head: [[sceneListBannerHeadCell as any]],
      body: listBody.length > 0 ? listBody : [[t('schedule.noBreakdown', { lng: language }), '', '', '', '', '']],
      styles: {
        font: fontName,
        fontSize: 10,
        cellPadding: { top: 0.8, right: 1, bottom: 0.8, left: 1 },
        overflow: 'linebreak',
        valign: 'top',
        textColor: SCHEDULE_DETAIL_BODY_TEXT_COLOR,
        lineColor: SCHEDULE_DETAIL_BODY_TEXT_COLOR,
        lineWidth: 0.2,
      },
      headStyles: {
        font: fontName,
        fontSize: SCENE_LIST_BANNER_FONT_PT,
        fontStyle: 'normal',
        fillColor: [245, 245, 245],
        textColor: 0,
        halign: 'left',
        valign: 'middle',
        lineWidth: 0.2,
        lineColor: SCHEDULE_DETAIL_BODY_TEXT_COLOR,
      },
      columnStyles: {
        0: { cellWidth: slotColumnWidths.code },
        1: { cellWidth: slotColumnWidths.col2 },
        2: { cellWidth: slotColumnWidths.col3 },
        3: { cellWidth: slotColumnWidths.col4 },
        4: { cellWidth: slotColumnWidths.col5 },
        5: { cellWidth: slotColumnWidths.col6 }
      },
      didParseCell: (data: any) => {
        if (data.section !== 'head' || data.row.index !== 0 || data.column.index !== 0) return;
        const raw = data.cell.raw;
        const isBanner =
          (raw && typeof raw === 'object' && (raw as { _scheduleDetailSceneBanner?: boolean })._scheduleDetailSceneBanner) ||
          data.cell.colSpan === 6;
        if (isBanner) {
          data.cell.styles.fontStyle = 'normal';
          data.cell.styles.overflow = () => [''];
        }
      },
      didDrawCell: (data: any) => {
        if (data.section !== 'head' || data.row.index !== 0 || data.column.index !== 0) return;
        const raw = data.cell.raw;
        const isBanner =
          (raw && typeof raw === 'object' && (raw as { _scheduleDetailSceneBanner?: boolean })._scheduleDetailSceneBanner) ||
          data.cell.colSpan === 6;
        if (!isBanner) return;
        drawScheduleDetailSceneListBannerRow(
          doc,
          language,
          fontName,
          data.cell,
          dateStr,
          secondaryLanguage,
          dayIndex,
          shootingDates.length,
          pageCountHeader
        );
      },
      didDrawPage: () => {
        addScheduleDetailDocHeader(doc, language, fontName, scheduleInfo, breakdownInfo, getCurrentDocPageNumber(doc));
      },
    });

    const slotsEndY = (doc as jsPDF & { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY || PAGE.START_Y + 40;
    const roleColW = tableWidthMm / 4;
    // 使用单元格正文 + linebreak 由 autotable 跨页排版；勿用空 content + didDrawCell，否则跨页续段不会重绘，第二页会出现大块空白。
    const roleMainText = roleSummary.mainNames.join('\n');
    const roleFeaturedText = buildNameQtyRows(roleSummary.featuredItems);
    const roleCrowdText = buildNameQtyRows(roleSummary.crowdItems);
    const roleActionText = buildNameQtyRows(roleSummary.actionItems);
    const roleTableStartY = adjustStartYIfTableHeaderWouldOrphan(doc, slotsEndY + 3, PAGE.MARGIN, 10);
    autoTable(doc, {
      startY: roleTableStartY,
      theme: 'grid',
      margin: { left: PAGE.MARGIN, right: PAGE.MARGIN, bottom: PAGE.MARGIN },
      tableWidth: tableWidthMm,
      showHead: 'firstPage',
      head: [[t('elementType.CAST', { lng: language }), t('elementType.FEATURED', { lng: language }), t('elementType.BACKGROUND', { lng: language }), t('elementType.STUNT_PLAYERS', { lng: language })]],
      body: [
        [
          { content: roleMainText, styles: { valign: 'top' } },
          { content: roleFeaturedText, styles: { valign: 'top' } },
          { content: roleCrowdText, styles: { valign: 'top' } },
          { content: roleActionText, styles: { valign: 'top' } }
        ],
        [
          { content: String(roleSummary.totals.main), styles: { halign: 'right' } },
          { content: String(roleSummary.totals.featured), styles: { halign: 'right' } },
          { content: String(roleSummary.totals.crowd), styles: { halign: 'right' } },
          { content: String(roleSummary.totals.action), styles: { halign: 'right' } }
        ]
      ],
      styles: {
        font: fontName,
        fontSize: 10,
        cellPadding: 1.2,
        overflow: 'linebreak',
        valign: 'top',
        textColor: SCHEDULE_DETAIL_BODY_TEXT_COLOR,
        lineColor: SCHEDULE_DETAIL_BODY_TEXT_COLOR,
        lineWidth: 0.2,
      },
      headStyles: {
        font: fontName,
        fontSize: 10,
        fontStyle: 'bold',
        fillColor: [245, 245, 245],
        textColor: 0,
        lineWidth: 0.2,
        lineColor: SCHEDULE_DETAIL_BODY_TEXT_COLOR,
      },
      bodyStyles: {
        lineWidth: 0.2,
        valign: 'top',
        textColor: SCHEDULE_DETAIL_BODY_TEXT_COLOR,
        lineColor: SCHEDULE_DETAIL_BODY_TEXT_COLOR,
      },
      columnStyles: {
        0: { cellWidth: roleColW },
        1: { cellWidth: roleColW },
        2: { cellWidth: roleColW },
        3: { cellWidth: roleColW }
      },
      didDrawPage: () => {
        addScheduleDetailDocHeader(doc, language, fontName, scheduleInfo, breakdownInfo, getCurrentDocPageNumber(doc));
      }
    });

    if (otherDeptRows.length > 0) {
      const castEndY = (doc as jsPDF & { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY || slotsEndY + 20;
      const typeColon = t('export.elementTypeListColon', { lng: language });
      const deptTableW = doc.internal.pageSize.getWidth() - PAGE.MARGIN * 2;
      const deptCellInnerW = deptTableW - 2 * 1.2;
      const deptVisualLines = buildScheduleDetailDeptVisualLines(
        doc,
        fontName,
        10,
        deptCellInnerW,
        typeColon,
        otherDeptRows
      );
      /** autotable 跨页切片 cell.text 后按序绘制片段；需在回调间保持位移 */
      let deptVisualDrawCursor = 0;

      const deptTableStartY = adjustStartYIfTableHeaderWouldOrphan(doc, castEndY + 3, PAGE.MARGIN, 10);
      autoTable(doc, {
        startY: deptTableStartY,
        theme: 'grid',
        margin: { left: PAGE.MARGIN, right: PAGE.MARGIN, bottom: PAGE.MARGIN },
        tableWidth: deptTableW,
        showHead: 'firstPage',
        head: [[t('export.shootScheduleDetailDeptNeedsHead', { lng: language })]],
        body: [
          [
            {
              content: deptVisualLines.map(() => '\u00a0').join('\n'),
              styles: {
                valign: 'top',
                overflow: (text: string | string[]) =>
                  (Array.isArray(text) ? text : [text]).map(() => ''),
              },
              _deptVisualLines: deptVisualLines,
            },
          ],
        ],
        styles: {
          font: fontName,
          fontSize: 10,
          cellPadding: 1.2,
          valign: 'top',
          textColor: SCHEDULE_DETAIL_BODY_TEXT_COLOR,
          lineColor: SCHEDULE_DETAIL_BODY_TEXT_COLOR,
          lineWidth: 0.2,
        },
        headStyles: {
          font: fontName,
          fontSize: 10,
          fontStyle: 'bold',
          fillColor: [245, 245, 245],
          textColor: 0,
          lineWidth: 0.2,
          lineColor: SCHEDULE_DETAIL_BODY_TEXT_COLOR,
          valign: 'middle',
          halign: 'left',
        },
        didDrawCell: (data: any) => {
          if (data.section !== 'body' || data.column.index !== 0) return;
          const raw = data.cell.raw as { _deptVisualLines?: ScheduleDetailDeptVisualLine[] } | undefined;
          const allLines = raw?._deptVisualLines;
          if (!allLines?.length) return;
          const lineCount = Array.isArray(data.cell.text) ? data.cell.text.length : 1;
          const slice = allLines.slice(deptVisualDrawCursor, deptVisualDrawCursor + lineCount);
          deptVisualDrawCursor += lineCount;
          drawScheduleDetailDeptVisualLineSlice(doc, fontName, 10, data.cell, slice);
        },
        didDrawPage: () => {
          addScheduleDetailDocHeader(doc, language, fontName, scheduleInfo, breakdownInfo, getCurrentDocPageNumber(doc));
        },
      });
    }
    const progress = 20 + Math.round(((idx + 1) / Math.max(1, shootingDates.length)) * 65);
    onProgress?.(Math.min(90, progress));
  });

  onProgress?.(90);
  return doc;
}
